import { Avatar, EmptyState, Input, Modal } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { cache } from '../../lib/cache';
import { useMe } from '../auth/AuthProvider';
import { useTranslation } from 'react-i18next';

export function NewDmDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { team, members, online } = useTeam();
  const me = useMe();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const { t } = useTranslation();

  const openDm = useMutation({
    mutationFn: (userId: string) => api.channels.openDm(team.id, userId),
    onSuccess: (channel) => {
      cache.upsertChannel(queryClient, channel);
      setQuery('');
      onClose();
      void navigate(`/app/t/${team.id}/c/${channel.id}`);
    },
  });

  const candidates = members.filter(
    (m) => m.id !== me.id && m.displayName.toLowerCase().includes(query.trim().toLowerCase()),
  );

  return (
    <Modal open={open} onClose={onClose} title={t('Nuevo mensaje directo')} size="sm">
      <Input
        autoFocus
        placeholder={t('Buscar persona…')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="mb-3"
      />
      {candidates.length === 0 ? (
        <EmptyState
          icon={<Users className="size-6" />}
          title={t('Nadie por aquí')}
          description={t('Invita a tu equipo desde «Miembros».')}
        />
      ) : (
        <ul className="flex flex-col">
          {candidates.map((member) => (
            <li key={member.id}>
              <button
                type="button"
                disabled={openDm.isPending}
                onClick={() => openDm.mutate(member.id)}
                className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left hover:bg-surface-2"
              >
                <Avatar
                  id={member.id}
                  name={member.displayName}
                  src={member.avatarUrl}
                  size="sm"
                  online={online ? online.has(member.id) : undefined}
                />
                <span className="text-sm">{member.displayName}</span>
                {member.title && (
                  <span className="truncate text-xs text-muted">{member.title}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
